import React, { useEffect, useState } from "react";
import { PageHeader } from "../components/PageHeader";
import api, { formatApiError, API } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Plus, Pencil, Trash, Download, FilePdf, Key, FolderOpen, X, CheckCircle } from "@phosphor-icons/react";
import PayslipModal from "../components/PayslipModal";
import CustomFieldsSection from "../components/CustomFieldsSection";

const DOC_SLOTS = [
  { key: "aadhaar", label: "Aadhaar" },
  { key: "pan", label: "PAN" },
  { key: "resume", label: "Resume" },
  { key: "address_proof", label: "Address Proof" },
  { key: "other", label: "Other" },
];

const EMPTY = {
  employee_id: "", name: "", email: "", phone: "", address: "",
  department: "", job_role: "", date_of_joining: "",
  password: "", security_question: "What is your favourite color?",
  security_answer: "", role: "employee",
  basic_salary: 0,
  custom_fields: {},
};

function autoPassword() {
  const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ";
  const lower = "abcdefghijkmnpqrstuvwxyz";
  const digits = "23456789";
  const sym = "@#!$&";
  const pool = upper + lower + digits + sym;
  const pick = (s) => s[Math.floor(Math.random() * s.length)];
  const arr = [pick(upper), pick(lower), pick(digits), pick(sym)];
  for (let i = 0; i < 8; i++) arr.push(pick(pool));
  return arr.sort(() => Math.random() - 0.5).join("");
}

export default function Employees() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [settings, setSettings] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [payslipFor, setPayslipFor] = useState(null);
  const [resetPassFor, setResetPassFor] = useState(null);
  const [docsFor, setDocsFor] = useState(null);

  const load = async () => {
    const [{ data: emps }, { data: st }] = await Promise.all([
      api.get("/employees"),
      api.get("/settings"),
    ]);
    setList(emps);
    setSettings(st);
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ ...EMPTY,
      department: settings?.department?.[0]?.value || "",
      job_role: settings?.job_role?.[0]?.value || "",
    });
    setShowForm(true);
  };

  const openEdit = (emp) => {
    setEditing(emp);
    setForm({ ...EMPTY, ...emp, password: "", security_answer: "" });
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        const payload = { name: form.name, email: form.email, phone: form.phone,
          department: form.department, job_role: form.job_role, date_of_joining: form.date_of_joining, role: form.role,
          basic_salary: parseFloat(form.basic_salary) || 0,
          custom_fields: form.custom_fields || {} };
        await api.put(`/employees/${editing.id}`, payload);
        toast.success("Employee updated");
      } else {
        await api.post("/employees", { ...form, basic_salary: parseFloat(form.basic_salary) || 0,
          custom_fields: form.custom_fields || {} });
        toast.success("Employee created");
      }
      setShowForm(false);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const del = async (emp) => {
    if (!window.confirm(`Delete ${emp.name}?`)) return;
    try { await api.delete(`/employees/${emp.id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const uploadResume = async (emp, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/employees/${emp.id}/resume`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Resume uploaded");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const downloadResume = async (emp) => {
    try {
      const res = await api.get(`/employees/${emp.id}/resume`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = emp.resume_filename || "resume"; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error("Download failed"); }
  };

  const docCount = (emp) => Object.keys(emp.documents || {}).filter((k) => (emp.documents || {})[k]).length;

  return (
    <div>
      <PageHeader
        eyebrow="Directory"
        title="Employees"
        subtitle="Register new hires, manage profiles and documents."
        actions={
          user?.role === "admin" && (
            <button onClick={openNew} data-testid="new-employee-btn"
                    className="brand-btn px-4 py-2.5 text-sm font-semibold inline-flex items-center gap-2">
              <Plus size={16} weight="bold" /> New Employee
            </button>
          )
        }
      />

      <div className="p-4 sm:p-6 lg:p-8">
        <div className="bg-white border border-[#E5E7EB] overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
              <tr className="text-left">
                {["Employee", "Department", "Job Role", "Email", "Documents", "Actions"].map((h) => (
                  <th key={h} className="px-4 py-3 label-caps">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {list.map((emp) => (
                <tr key={emp.id} className="border-b border-[#F3F4F6] hover:bg-[#FAFAFB]" data-testid={`row-emp-${emp.employee_id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <EmployeeAvatar emp={emp} />
                      <div>
                        <div className="font-semibold text-[#111827]">{emp.name}</div>
                        <div className="text-xs text-[#6B7280] font-mono">{emp.employee_id} · {emp.role}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">{emp.department}</td>
                  <td className="px-4 py-3">{emp.job_role}</td>
                  <td className="px-4 py-3 text-[#6B7280]">{emp.email}</td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => setDocsFor(emp)}
                      data-testid={`view-docs-${emp.employee_id}`}
                      className="inline-flex items-center gap-1.5 text-xs text-[#106109] hover:underline"
                    >
                      <FolderOpen size={14} weight="fill" />
                      <span className="font-semibold">{docCount(emp)}/5</span> uploaded
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {user?.role === "admin" && (
                        <>
                          <button onClick={() => setPayslipFor({ ...emp, can_edit_salary: true })} className="border border-[#E5E7EB] p-1.5 hover:bg-[#F0F5FF] text-[#106109]" data-testid={`payslip-emp-${emp.employee_id}`} title="Payslip">
                            <FilePdf size={14} />
                          </button>
                          <button onClick={() => setResetPassFor(emp)} className="border border-[#E5E7EB] p-1.5 hover:bg-[#FEF3C7] text-[#92400E]" data-testid={`reset-pass-${emp.employee_id}`} title="Reset password">
                            <Key size={14} />
                          </button>
                          <button onClick={() => openEdit(emp)} className="border border-[#E5E7EB] p-1.5 hover:bg-[#F9FAFB]" data-testid={`edit-emp-${emp.employee_id}`}>
                            <Pencil size={14} />
                          </button>
                          <button onClick={() => del(emp)} className="border border-[#E5E7EB] p-1.5 hover:bg-[#FEF2F2] text-[#DC2626]" data-testid={`delete-emp-${emp.employee_id}`}>
                            <Trash size={14} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {list.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-[#9CA3AF]">No employees yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="employee-form-modal">
          <div className="bg-white w-full max-w-2xl border border-[#E5E7EB] max-h-[90vh] overflow-y-auto">
            <div className="border-b border-[#E5E7EB] px-6 py-4 flex items-center justify-between">
              <div>
                <div className="label-caps">{editing ? "Update" : "Register"}</div>
                <h3 className="font-display font-bold text-xl">{editing ? "Edit Employee" : "New Employee"}</h3>
              </div>
              <button onClick={() => setShowForm(false)} className="text-[#6B7280]">✕</button>
            </div>
            <form onSubmit={submit} className="p-6 space-y-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Employee ID" required disabled={!!editing}>
                <input required disabled={!!editing} value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value.toUpperCase() })}
                       data-testid="emp-form-id" className="input" />
              </Field>
              <Field label="Full Name" required>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="emp-form-name" className="input" />
              </Field>
              <Field label="Email" required>
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="emp-form-email" className="input" />
              </Field>
              <Field label="Phone">
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input" />
              </Field>
              <Field label="Department" required>
                <select required value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} data-testid="emp-form-department" className="input">
                  <option value="">Select…</option>
                  {settings?.department?.map((d) => <option key={d.id} value={d.value}>{d.value}</option>)}
                </select>
              </Field>
              <Field label="Job Role" required>
                <select required value={form.job_role} onChange={(e) => setForm({ ...form, job_role: e.target.value })} data-testid="emp-form-role" className="input">
                  <option value="">Select…</option>
                  {settings?.job_role?.map((d) => <option key={d.id} value={d.value}>{d.value}</option>)}
                </select>
              </Field>
              <Field label="Date of Joining">
                <input type="date" value={form.date_of_joining || ""} onChange={(e) => setForm({ ...form, date_of_joining: e.target.value })} className="input" />
              </Field>
              <Field label="Role">
                <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="input">
                  <option value="employee">Employee</option>
                  <option value="admin">Admin</option>
                </select>
              </Field>
              <Field label="Basic Salary (₹ / month)">
                <input type="number" min="0" step="100" value={form.basic_salary}
                       onChange={(e) => setForm({ ...form, basic_salary: e.target.value })}
                       data-testid="emp-form-salary" className="input" />
              </Field>
              <CustomFieldsSection entity="employee" values={form.custom_fields || {}}
                                    onChange={(v) => setForm({ ...form, custom_fields: v })} />
              {!editing && (
                <>
                  <Field label="Password" required>
                    <div className="flex gap-2 items-stretch">
                      <input required type="text" minLength={6} value={form.password}
                             onChange={(e) => setForm({ ...form, password: e.target.value })}
                             data-testid="emp-form-password" className="input flex-1" />
                      <button type="button" data-testid="emp-form-autopass"
                              onClick={() => setForm({ ...form, password: autoPassword() })}
                              className="px-3 border border-[#106109] text-[#106109] text-xs font-semibold hover:bg-[#F0F7EF]"
                              title="Generate a strong password">
                        Auto-generate
                      </button>
                    </div>
                    <div className="text-[11px] text-[#6B7280] mt-1">
                      Share this password with the employee securely. They can change it after login.
                    </div>
                  </Field>
                  <Field label="Security Question" required>
                    <input required value={form.security_question} onChange={(e) => setForm({ ...form, security_question: e.target.value })} className="input" />
                  </Field>
                  <Field label="Security Answer" required>
                    <input required value={form.security_answer} onChange={(e) => setForm({ ...form, security_answer: e.target.value })} data-testid="emp-form-secans" className="input" />
                  </Field>
                </>
              )}
              <div className="md:col-span-2 flex gap-3 pt-4">
                <button type="button" onClick={() => setShowForm(false)} className="flex-1 py-2.5 text-sm border border-[#E5E7EB]">Cancel</button>
                <button type="submit" className="brand-btn flex-1 py-2.5 text-sm font-semibold" data-testid="emp-form-save">
                  {editing ? "Save changes" : "Create Employee"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <style>{`.input{width:100%;border:1px solid #E5E7EB;padding:.55rem .75rem;font-size:.875rem;outline:none;transition:border-color 200ms}.input:focus{border-color:#106109;box-shadow:0 0 0 3px rgba(0,47,167,.15)}`}</style>

      {payslipFor && (
        <PayslipModal employee={payslipFor} onClose={() => setPayslipFor(null)}
                      onSaved={() => { load(); setPayslipFor(null); }} />
      )}

      {resetPassFor && (
        <ResetPasswordModal
          employee={resetPassFor}
          onClose={() => setResetPassFor(null)}
          onSaved={() => setResetPassFor(null)}
        />
      )}

      {docsFor && (
        <DocumentsModal employee={docsFor} onClose={() => setDocsFor(null)} />
      )}
    </div>
  );
}

function EmployeeAvatar({ emp }) {
  const src = emp?.photo_path ? `${API}/employees/${emp.id}/photo` : null;
  const initials = (emp?.name || "")
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase() || "?";
  return (
    <div className="w-9 h-9 rounded-full overflow-hidden bg-[#106109]/10 border border-[#106109]/25 flex items-center justify-center flex-shrink-0" data-testid={`avatar-${emp.employee_id}`}>
      {src ? (
        <img src={src} alt={emp?.name || "avatar"} className="w-full h-full object-cover" />
      ) : (
        <span className="text-[11px] font-display font-black text-[#106109]">{initials}</span>
      )}
    </div>
  );
}

function DocumentsModal({ employee, onClose }) {
  const [emp, setEmp] = useState(employee);
  const load = () =>
    api.get(`/employees/${employee.id}`).then(({ data }) => setEmp(data)).catch(() => {});

  const downloadDoc = async (slotKey, slotLabel, entry) => {
    try {
      const res = await api.get(`/employees/${employee.id}/documents/${slotKey}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = entry?.filename || slotLabel; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error("Download failed"); }
  };

  const removeDoc = async (slotKey, slotLabel) => {
    if (!window.confirm(`Remove ${slotLabel} for ${employee.name}?`)) return;
    try {
      await api.delete(`/employees/${employee.id}/documents/${slotKey}`);
      toast.success(`${slotLabel} removed`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const docs = emp?.documents || {};
  const uploadedCount = Object.keys(docs).filter((k) => docs[k]).length;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="documents-modal">
      <div className="bg-white w-full max-w-lg border border-[#E5E7EB] max-h-[90vh] overflow-y-auto">
        <div className="border-b border-[#E5E7EB] px-6 py-4 flex items-start justify-between gap-4">
          <div>
            <div className="label-caps">KYC · Documents</div>
            <h3 className="font-display font-black text-2xl tracking-tight mt-1">{employee.name}</h3>
            <div className="text-xs text-[#6B7280] font-mono mt-1">{employee.employee_id} · {uploadedCount}/5 uploaded</div>
          </div>
          <button onClick={onClose} data-testid="documents-modal-close" className="text-[#6B7280] hover:text-[#111827]"><X size={20} /></button>
        </div>
        <ul className="divide-y divide-[#F3F4F6]">
          {DOC_SLOTS.map((slot) => {
            const entry = docs[slot.key];
            return (
              <li key={slot.key} className="px-6 py-3 flex items-center gap-3" data-testid={`admin-doc-row-${slot.key}`}>
                <div className="flex-1">
                  <div className="text-sm font-semibold text-[#111827]">{slot.label}</div>
                  {entry ? (
                    <div className="text-xs text-[#6B7280] mt-0.5">
                      {entry.filename} · {Math.max(1, Math.round((entry.size || 0) / 1024))} KB
                    </div>
                  ) : (
                    <div className="text-xs text-[#9CA3AF] italic mt-0.5">Not uploaded</div>
                  )}
                </div>
                {entry && <CheckCircle size={16} weight="fill" className="text-[#10B981]" />}
                {entry && (
                  <>
                    <button
                      onClick={() => downloadDoc(slot.key, slot.label, entry)}
                      data-testid={`admin-doc-download-${slot.key}`}
                      className="border border-[#E5E7EB] p-2 hover:bg-[#F9FAFB]" title="Download">
                      <Download size={14} />
                    </button>
                    <button
                      onClick={() => removeDoc(slot.key, slot.label)}
                      data-testid={`admin-doc-remove-${slot.key}`}
                      className="border border-[#E5E7EB] p-2 hover:bg-[#FEF2F2] text-[#DC2626]" title="Remove">
                      <Trash size={14} />
                    </button>
                  </>
                )}
              </li>
            );
          })}
        </ul>
        <div className="px-6 py-4 border-t border-[#E5E7EB] text-xs text-[#6B7280]">
          The employee uploads and replaces these files themselves from <strong>My Profile</strong>. Admin can download or remove for compliance.
        </div>
      </div>
    </div>
  );
}

function ResetPasswordModal({ employee, onClose, onSaved }) {
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (pwd !== pwd2) { toast.error("Passwords do not match"); return; }
    setSaving(true);
    try {
      await api.post(`/employees/${employee.id}/reset-password`, { new_password: pwd });
      toast.success(`Password reset for ${employee.name}`);
      onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="reset-pass-modal">
      <form onSubmit={submit} className="bg-white w-full max-w-md border border-[#E5E7EB]">
        <div className="border-b border-[#E5E7EB] px-6 py-4">
          <div className="label-caps">Admin action</div>
          <h3 className="font-display font-bold text-xl">Reset password</h3>
          <div className="text-xs text-[#6B7280] mt-1">{employee.name} ({employee.employee_id})</div>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="label-caps block mb-1.5">New password</label>
            <input type="password" required minLength={6} value={pwd} onChange={(e) => setPwd(e.target.value)}
                   data-testid="reset-pass-new" className="w-full border border-[#E5E7EB] px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="label-caps block mb-1.5">Confirm new password</label>
            <input type="password" required minLength={6} value={pwd2} onChange={(e) => setPwd2(e.target.value)}
                   data-testid="reset-pass-confirm" className="w-full border border-[#E5E7EB] px-3 py-2 text-sm" />
          </div>
          <div className="text-xs text-[#6B7280] bg-[#FEF3C7] border border-[#FDE68A] p-2">
            The employee will use this new password on their next login. Please share it with them via a secure channel.
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 text-sm border border-[#E5E7EB]">Cancel</button>
            <button type="submit" disabled={saving} data-testid="reset-pass-save"
                    className="brand-btn flex-1 py-2.5 text-sm font-semibold disabled:opacity-60">
              {saving ? "Resetting…" : "Reset password"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="label-caps block mb-1.5">{label}{required && <span className="text-[#DC2626]"> *</span>}</label>
      {children}
    </div>
  );
}
